/**
 * File: DropDownField.js
 * Origin: isotect
 * Description: DropDownField component for forms.
 *
 * Version: v2.1.1
 * Created: 2025-12-29
 * Last Updated: 2026-07-26
 *
 * Used By: isotect
 * Maintainer: Zachary Dicks
 *
 * Notes:
 * - Changes in this file may affect all consuming frontend modules.
 * - See Git repository 'isotect' for full change history.
 */

/**
 * DropDownField component renders a controlled select input with an associated label.
 *
 * @component
 *
 * @param {Object} props - Component properties
 * @param {string} props.label - Label text displayed with the select field
 * @param {Array<{id: string | number, value: string}>} props.options - List of options to render in the select field
 * @param {string | number} props.selection - Currently selected option id
 * @param {(id: string | number) => void} props.setSelection - Callback to update the selected option id
 * @param {boolean} [props.isRequired=true] - Whether the field must be completed before the form can be submitted
 *
 * @returns {JSX.Element} A select input element with an associated label
 *
 * @example
 * <DropDownField
 *   label="Subject"
 *   options={[
 *     { id: 1, value: "Option A" },
 *     { id: 2, value: "Option B" }
 *   ]}
 *   selection={selection}
 *   setSelection={setSelection}
 *   isRequired={false}
 * />
 */
function DropDownField({
    label,
    options,
    selection,
    setSelection,
    isRequired = true,
}) {
    const id = `select-${label.replace(/\s+/g, "-").toLowerCase()}`;

    const handleChange = (e) => {
        setSelection(e.target.value);
    };

    return (
        <>
            <div className="drop-down-field">
                {label && <label htmlFor={id}>{label}</label>}
                <select
                    id={id}
                    value={selection}
                    onChange={handleChange}
                    required={isRequired}
                >
                    <option value="" disabled hidden>
                        -- Select --
                    </option>
                    {options.map((option) => (
                        <option key={option.id} value={option.id}>
                            {option.value}
                        </option>
                    ))}
                </select>
            </div>
        </>
    );
}

export default DropDownField;
